import { type Vector3Like } from 'three'
import type { ProcedureItem } from './procedure-api.type'

export enum EModelVersion {
  male_v1 = 'male_v1',
  female_v1 = 'female_v1',
  male_v2 = 'male_v2',
  female_v2 = 'female_v2',
}

export const DEFAULT_MODEL_VERSION: EModelVersion = EModelVersion.female_v2

export enum EGender {
  MALE = 'male',
  FEMALE = 'female',
}

export enum DrawMode {
  FREE_LINE = 'free-line',
  STRAIGHT_LINE = 'straight-line',
  X = 'x',
  MEASURE = 'measure',
  PIN = 'pin',
  ERASER = 'eraser',
}

export enum DrawLineState {
  IDLE = 'idle',
  START_POINT = 'startPoint',
  END_POINT = 'endPoint',
}

export enum DrawingElementVisibleState {
  HIDDEN = 'hidden', // User pressed undo - can be redone
  VISIBLE = 'visible', // FE created and visible - can be undone
  PERSISTED = 'persisted', // From BE - always visible, cannot be undone
}

export enum ProcedureStoreTagTypes {
  Procedures = 'Procedures',
  ProcedureCodes = 'ProcedureCodes',
  Products = 'Products',
  SoapNotes = 'SoapNotes',
  EncounterPhotos = 'EncounterPhotos',
  SoapImages = 'SoapImages',
  ESignForm = 'ESignForm',
  SignedDocuments = 'SignedDocuments',
  IntakeTemplates = 'IntakeTemplates',
  EncounterCheckout = 'EncounterCheckout',
  StripeTerminals = 'StripeTerminals',
  PhysicianIntroductions = 'PhysicianIntroductions',
}

export type DrawLineStateType = (typeof DrawLineState)[keyof typeof DrawLineState]
export type DrawModeType = (typeof DrawMode)[keyof typeof DrawMode] | null

export type CameraAnimationConfig = {
  position: Vector3Like
  target: Vector3Like
  animate?: boolean
  duration?: number
}

export type SelectedElementWithPosition = {
  element: DrawingElement
  tooltipPosition?: Vector3Like
}

export type ProcedureSliceType = {
  scene: {
    position: Vector3Like
    target: Vector3Like
  }
  desiredCameraPosition: CameraAnimationConfig | null
  drawingElements: DrawingElement[]
  selectedElement: SelectedElementWithPosition | null
  drawMode: DrawModeType
  drawLineState: DrawLineStateType
  selectedEncounterUid: string | null
  selectedComparisonEncounterUid: string | null
  selectedPatientId: number | null
  selectedProcedure: ProcedureItem | null
  selectedLabId: number | null
  selectedFormDocId: number | null
  showElementIds: boolean
  modelVersion: EModelVersion | null
  isPrinting: boolean
  isModelSceneExpanded: boolean
  isDrawCustomizerOpen: boolean
  deletingProcedureId: number | null
  isProcedureDeletePending: boolean
}

export type SceneType = {
  position: Vector3Like
  target: Vector3Like
}

export type DrawingElementDescriptionData = {
  text: string
  isUnitsChecked: boolean
}

export type DrawingElementDescription = string | DrawingElementDescriptionData

export type DrawingElement = {
  id: string
  clientKey?: string
  drawMode: DrawMode
  points: Vector3Like[]
  color: string
  size: number
  visibleState: DrawingElementVisibleState
  description?: DrawingElementDescription
  descriptionPosition?: Vector3Like
  procedureId?: number
  timestamp: number
  isModified?: boolean
  camera?: {
    position: Vector3Like
    target: Vector3Like
  }
}

export enum ProcedureTypeCode {
  CPT4 = 'CPT4',
  ICD10 = 'ICD10',
}
